// src/pages/Signup.js
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../api/api";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import GoogleButton from "react-google-button"; // ✅ Import Google button
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";

const Signup = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordChecks = {
    minLength: formData.password.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.password),
    hasLowercase: /[a-z]/.test(formData.password),
    hasNumber: /\d/.test(formData.password),
    hasSpecial: /[^A-Za-z0-9]/.test(formData.password),
  };

  const isPasswordStrong = Object.values(passwordChecks).every(Boolean);
  const hasConfirmPassword = formData.confirmPassword.length > 0;
  const passwordsMatch = formData.password === formData.confirmPassword;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGoogleSignup = () => {
    const backendUrl =
      process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
    window.location.href = `${backendUrl}/api/auth/google`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordStrong) {
      alert(
        t(
          "auth.signup.passwordWeak",
          "Please choose a stronger password that meets all requirements.",
        ),
      );
      return;
    }

    if (!passwordsMatch) {
      alert(
        t(
          "auth.signup.passwordsDoNotMatch",
          "Passwords do not match. Please confirm your password.",
        ),
      );
      return;
    }

    try {
      const response = await api.post("/auth/signup", {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
      });
      if (response.data?.user) {
        login(response.data.user);
      }
      alert(
        response.data?.message ||
          t("auth.signup.success", "Signup successful!"),
      );
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Signup error:", error);
      alert(error.response?.data?.message || t("auth.signup.error"));
    }
  };

  return (
    <div className="pt-[4.5rem] lg:pt-[7rem] lg:pb-[5rem] h-auto flex items-center justify-center bg-gray-100">
      <div className=" bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          {t("auth.signup.title")}
        </h2>

        {/* ✅ Google Signup Button */}
        <div className="mb-4 flex justify-center">
          <GoogleButton
            onClick={handleGoogleSignup}
            label={t("auth.signup.googleButton")}
          />
        </div>

        {/* ✅ Separator */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-white px-2 text-gray-500">
              {t("auth.common.orContinueWith")}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* First Name */}
          <div className="mb-4">
            <label
              htmlFor="first_name"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              {t("auth.signup.firstName")}
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Last Name */}
          <div className="mb-4">
            <label
              htmlFor="last_name"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              {t("auth.signup.lastName")}
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Email */}
          <div className="mb-4">
            <label
              htmlFor="email"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              {t("auth.common.email")}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Password */}
          <div className="mb-4">
            <label
              htmlFor="password"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              {t("auth.common.password")}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <div className="mt-3 space-y-1 text-xs">
              <p
                className={
                  passwordChecks.minLength ? "text-green-600" : "text-gray-500"
                }
              >
                • {t("auth.signup.ruleMinLength", "At least 8 characters")}
              </p>
              <p
                className={
                  passwordChecks.hasUppercase
                    ? "text-green-600"
                    : "text-gray-500"
                }
              >
                •{" "}
                {t(
                  "auth.signup.ruleUppercase",
                  "At least one uppercase letter (A-Z)",
                )}
              </p>
              <p
                className={
                  passwordChecks.hasLowercase
                    ? "text-green-600"
                    : "text-gray-500"
                }
              >
                •{" "}
                {t(
                  "auth.signup.ruleLowercase",
                  "At least one lowercase letter (a-z)",
                )}
              </p>
              <p
                className={
                  passwordChecks.hasNumber ? "text-green-600" : "text-gray-500"
                }
              >
                • {t("auth.signup.ruleNumber", "At least one number (0-9)")}
              </p>
              <p
                className={
                  passwordChecks.hasSpecial ? "text-green-600" : "text-gray-500"
                }
              >
                •{" "}
                {t(
                  "auth.signup.ruleSpecial",
                  "At least one special character (!@#$...)",
                )}
              </p>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="mb-6">
            <label
              htmlFor="confirmPassword"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              {t("auth.signup.confirmPassword", "Confirm Password")}
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <span
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            {hasConfirmPassword && !passwordsMatch && (
              <p className="mt-2 text-xs text-red-600">
                {t(
                  "auth.signup.passwordsDoNotMatch",
                  "Passwords do not match. Please confirm your password.",
                )}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={!isPasswordStrong || !passwordsMatch}
            className="w-full bg-blue-500 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:shadow-outline"
          >
            {t("auth.signup.submit")}
          </button>
        </form>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            {t("auth.signup.haveAccount", "Already have an account?")}{" "}
            <Link
              to="/login"
              className="text-blue-500 hover:text-blue-700 hover:underline font-medium"
            >
              {t("auth.signup.signInNow", "Sign in now")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
